import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUserPlus, FaTimes, FaEdit, FaBan, FaCheckCircle, FaChartLine, FaFileCsv, FaTrophy, FaFileAlt, FaUpload } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import './MyAgents.css';

// ══════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════
const API = 'https://digitalapi.definiteassurance.com/api';
const COMPANY = 'Definite';

const REGIONS = [
  { value: '437', label: 'Bungoma' },
  { value: '639', label: 'Development House' },
  { value: '641', label: 'Eldoret' },
  { value: '588', label: 'Embu' },
  { value: '630', label: 'Head Office' },
  { value: '447', label: 'Kiambu' },
  { value: '593', label: 'Kakamega' },
  { value: '626', label: 'Kapsabet' },
  { value: '594', label: 'Kericho' },
  { value: '598', label: 'Kisii' },
  { value: '599', label: 'Kisumu' },
  { value: '640', label: 'Kitale' },
  { value: '604', label: 'Machakos' },
  { value: '642', label: 'Malindi' },
  { value: '608', label: 'Meru' },
  { value: '609', label: 'Migori' },
  { value: '610', label: 'Mombasa' },
  { value: '611', label: "Murang'a" },
  { value: '615', label: 'Narok' },
  { value: '644', label: 'Nyahururu' },
  { value: '618', label: 'Nyeri' },
  { value: '613', label: 'Nakuru' },
  { value: '645', label: 'Naivasha' },
  { value: '638', label: 'Thika' },
];

// Product codes match Proposals.Product / Products.OID — mirrors the list used on Get Cover.
const PRODUCTS = [
  { code: 3617, name: 'Motor Private' },
  { code: 3582, name: 'Private Hire - Chauffeur Driven' },
  { code: 3583, name: 'Private Hire - Self Drive' },
  { code: 4694, name: 'Motor Private TOR' },
  { code: 4638, name: 'PSV MATATU (7-35)' },
  { code: 3587, name: 'PSV BUS (36-105)' },
  { code: 4652, name: 'PSV Boda Boda' },
  { code: 4655, name: 'PSV Tuk Tuk' },
  { code: 4630, name: 'Motor Commercial' },
  { code: 3604, name: 'Tuk Tuk Commercial' },
  { code: 4695, name: 'Motor Commercial TOR' },
  { code: 4653, name: 'Motorcycle Private' },
];

const EMPTY_FORM = {
  name: '',
  email: '',
  phone: '',
  identification: '',
  krapin: '',
  selectedRegion: '',
  region: '',
  branch: '',
};

const EMPTY_EDIT_FORM = {
  name: '',
  email: '',
  phone: '',
  identification: '',
  krapin: '',
  selectedRegion: '',
  region: '',
  branch: '',
};

// ══════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════
const formatter = (amount) =>
  Number(amount || 0).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');

const formatISODate = (input) => {
  try {
    if (typeof input === 'string') return input.split('T')[0];
    if (input instanceof Date) return input.toISOString().split('T')[0];
    return '--';
  } catch { return '--'; }
};

const localPhone = (mobile) => {
  if (!mobile) return '';
  return mobile.startsWith('254') ? `0${mobile.slice(3)}` : mobile;
};

// ══════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════
export default function MyAgents() {
  const navigate = useNavigate();

  // ── Auth & agent ──
  const [credentials, setCredentials] = useState(null);
  const [isAuth, setIsAuth] = useState(false);
  const [agent, setAgent] = useState('');
  const [agentCode, setAgentCode] = useState('');
  const [agentName, setAgentName] = useState('');
  const [isSuperAgent, setIsSuperAgent] = useState(null); // null = still checking

  // ── Add Agent panel ──
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [kycFiles, setKycFiles] = useState([]); // File[] selected for the new agent's KYC folder
  const [buttonText, setButtonText] = useState('Register Agent');

  // Region/Branch (IntermediaryBranches) for the Add Agent form
  const [branchOptions, setBranchOptions] = useState([]); // [{OID, Region, BranchName}]

  // ── Agents list ──
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);

  // ── Edit agent ──
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT_FORM);
  const [editButtonText, setEditButtonText] = useState('Save Changes');
  const [editKycFiles, setEditKycFiles] = useState([]);       // KYC filenames already on disk for editTarget
  const [editKycLoading, setEditKycLoading] = useState(false);
  const [editKycNewFiles, setEditKycNewFiles] = useState([]); // File[] queued to upload from the Edit modal
  const [editKycUploading, setEditKycUploading] = useState(false);

  // ── Activate / Deactivate ──
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [statusButtonText, setStatusButtonText] = useState('');

  // ── Production ──
  const [showProductionModal, setShowProductionModal] = useState(false);
  const [productionTarget, setProductionTarget] = useState(null);
  const [productionRows, setProductionRows] = useState([]);
  const [productionLoading, setProductionLoading] = useState(false);
  const [productionProduct, setProductionProduct] = useState('');
  const [productionStart, setProductionStart] = useState('');
  const [productionEnd, setProductionEnd] = useState('');

  // ══════════════════════════════════════
  //  API HELPER
  // ══════════════════════════════════════
  const authHeaders = useCallback(() => ({
    'Authorization': `Basic ${credentials}`,
    'Content-Type': 'application/json',
  }), [credentials]);

  const apiFetch = useCallback(async (url, options = {}) => {
    const res = await fetch(url, {
      method: options.method || 'GET',
      headers: authHeaders(),
      ...options,
    });
    return res.json();
  }, [authHeaders]);

  // ══════════════════════════════════════
  //  INIT — credentials, agent
  // ══════════════════════════════════════
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/getCredentials?username=${COMPANY}`);
        const data = await res.json();
        setCredentials(btoa(`${data.Username}:${data.Password}`));
        setIsAuth(true);
      } catch (err) { toast.error('Failed to load credentials'); }
    })();
    const user = localStorage.getItem('definiteagent');
    if (user) setAgent(user);
    else navigate('/login');
  }, [navigate]);

  // Agent details
  useEffect(() => {
    if (!agent || !isAuth) return;
    (async () => {
      try {
        const dealer = await apiFetch(`${API}/getDealerdetails?email=${agent}`);
        setAgentCode(dealer.OID);
        setAgentName(dealer.Dealer_name);
        setIsSuperAgent(dealer.IsSuperAgent === 1);
      } catch (err) {
        toast.error('Failed to load agent details');
        setIsSuperAgent(false);
      }
    })();
  }, [agent, isAuth, apiFetch]);

  // ── Restrict this page to super agents only ──
  useEffect(() => {
    if (isSuperAgent === false) {
      toast.error('You do not have access to My Agents');
      navigate('/dashboard');
    }
  }, [isSuperAgent, navigate]);

  // ══════════════════════════════════════
  //  AGENTS LIST
  // ══════════════════════════════════════
  const fetchMyAgents = useCallback(async () => {
    if (!agentCode || !isAuth) return;
    try {
      setAgentsLoading(true);
      const data = await apiFetch(`${API}/getMyAgents?agent=${agentCode}`);
      setAgents(Array.isArray(data) ? data : []);
    } catch (err) { toast.error('Failed to load agents'); }
    finally { setAgentsLoading(false); }
  }, [agentCode, isAuth, apiFetch]);

  useEffect(() => { fetchMyAgents(); }, [fetchMyAgents]);

  // ══════════════════════════════════════
  //  REGION / BRANCH — IntermediaryBranches, for the Add Agent form
  // ══════════════════════════════════════
  useEffect(() => {
    if (!isAuth) return;
    (async () => {
      try {
        const data = await apiFetch(`${API}/getIntermediaryBranches`);
        setBranchOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load branches', err);
      }
    })();
  }, [isAuth, apiFetch]);

  const regionOptions = useMemo(
    () => [...new Set(branchOptions.map((b) => b.Region))],
    [branchOptions]
  );

  const branchesForRegion = useMemo(
    () => branchOptions.filter((b) => b.Region === form.region),
    [branchOptions, form.region]
  );

  // ══════════════════════════════════════
  //  ADD AGENT — replicates the public Register flow, tagged with the creating agent
  // ══════════════════════════════════════
  const updateField = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  // Changing Region invalidates whatever Branch was previously picked.
  const updateFormRegion = (e) => setForm((prev) => ({ ...prev, region: e.target.value, branch: '' }));

  const toggleAddForm = () => {
    setShowAddForm((prev) => !prev);
    setForm(EMPTY_FORM);
    setKycFiles([]);
  };

  const handleKycFilesChange = (e) => setKycFiles(Array.from(e.target.files || []));

  // Uploads one or more KYC files for `agentOid` into uploads/<agentOid>/KYC/ (server-side).
  const uploadKycFiles = useCallback(async (agentOid, filesToUpload) => {
    const formData = new FormData();
    formData.append('agent', agentOid);
    filesToUpload.forEach((file) => formData.append('files', file));

    const res = await fetch(`${API}/uploadAgentKYC`, {
      method: 'POST',
      headers: { Authorization: `Basic ${credentials}` }, // no Content-Type — browser sets multipart boundary
      body: formData,
    });
    return res.json();
  }, [credentials]);

  const handleAddAgent = useCallback(async (e) => {
    e.preventDefault();
    const { name, email, phone, identification, krapin, selectedRegion, branch } = form;

    if (!name || !email || !phone || !identification || !krapin) {
      toast.error('All fields marked * are required!');
      return;
    }

    try {
      setButtonText('Please wait...');
      const data = await apiFetch(`${API}/register`, {
        method: 'POST',
        body: JSON.stringify({
          name, email, phone, identification, krapin, selectedRegion,
          selectedBranch: branch,
          creatingAgent: agentCode,
        }),
      });

      if (data.success && !data.exists) {
        if (kycFiles.length > 0 && data.OID) {
          try {
            await uploadKycFiles(data.OID, kycFiles);
          } catch (err) {
            toast.error('Agent created, but KYC document upload failed.');
          }
        }
        toast.success('Agent successfully registered. Login details sent to their email.');
        setShowAddForm(false);
        setForm(EMPTY_FORM);
        setKycFiles([]);
        fetchMyAgents();
      } else if (!data.success && data.exists) {
        toast.error('An account with these details already exists');
      } else {
        toast.error('Something went wrong! Please try again later');
      }
    } catch (err) {
      toast.error('Network error — please try again');
    } finally {
      setButtonText('Register Agent');
    }
  }, [form, kycFiles, agentCode, apiFetch, fetchMyAgents, uploadKycFiles]);

  // ══════════════════════════════════════
  //  EDIT AGENT
  // ══════════════════════════════════════
  const openEditModal = useCallback((row) => {
    setEditTarget(row);
    setEditForm({
      name: row.Dealer_name || '',
      email: row.Email || '',
      phone: localPhone(row.Mobile),
      identification: row.IdentificationNo || '',
      krapin: row.DealerPin || '',
      selectedRegion: row.Companyy ? String(row.Companyy) : '',
      region: row.BranchRegion || '',
      branch: row.IntermediaryBranch ? String(row.IntermediaryBranch) : '',
    });
    setEditKycFiles([]);
    setEditKycNewFiles([]);
    setShowEditModal(true);
  }, []);

  // KYC files list for the agent currently open in the Edit modal. A missing/empty KYC
  // folder is a normal case (agent has no uploads yet), not an error, so it's treated as
  // "no files" rather than surfacing a toast.
  const fetchEditKycFiles = useCallback(async (agentOid) => {
    setEditKycLoading(true);
    try {
      const res = await fetch(`${API}/files/${agentOid}/KYC`);
      if (!res.ok) { setEditKycFiles([]); return; }
      const data = await res.json();
      setEditKycFiles(Array.isArray(data.files) ? data.files : []);
    } catch {
      setEditKycFiles([]);
    } finally {
      setEditKycLoading(false);
    }
  }, []);

  // Fetched fresh each time the modal opens.
  useEffect(() => {
    if (!showEditModal || !editTarget) return;
    fetchEditKycFiles(editTarget.OID);
  }, [showEditModal, editTarget, fetchEditKycFiles]);

  const openKycFile = useCallback((fileName) => {
    const fileUrl = `${API.replace('/api', '')}/uploads/${editTarget.OID}/KYC/${fileName}`;
    window.open(fileUrl, '_blank');
  }, [editTarget]);

  const handleEditKycFilesChange = (e) => setEditKycNewFiles(Array.from(e.target.files || []));

  const handleUploadEditKyc = useCallback(async () => {
    if (!editTarget || editKycNewFiles.length === 0) return;
    setEditKycUploading(true);
    try {
      const data = await uploadKycFiles(editTarget.OID, editKycNewFiles);
      if (data.success) {
        toast.success('KYC documents uploaded');
        setEditKycNewFiles([]);
        fetchEditKycFiles(editTarget.OID);
      } else {
        toast.error(data.message || 'Failed to upload documents');
      }
    } catch (err) {
      toast.error('Network error — please try again');
    } finally {
      setEditKycUploading(false);
    }
  }, [editTarget, editKycNewFiles, uploadKycFiles, fetchEditKycFiles]);

  const updateEditField = (field) => (e) => setEditForm((prev) => ({ ...prev, [field]: e.target.value }));

  // Changing KCB Region invalidates whatever KCB Branch was previously picked.
  const updateEditFormRegion = (e) => setEditForm((prev) => ({ ...prev, region: e.target.value, branch: '' }));

  const editBranchesForRegion = useMemo(
    () => branchOptions.filter((b) => b.Region === editForm.region),
    [branchOptions, editForm.region]
  );

  const handleEditAgent = useCallback(async (e) => {
    e.preventDefault();
    if (!editTarget) return;
    const { name, email, phone, selectedRegion, branch } = editForm;

    if (!name || !email || !phone) {
      toast.error('All fields marked * are required!');
      return;
    }

    try {
      setEditButtonText('Saving...');
      const data = await apiFetch(`${API}/updateAgent`, {
        method: 'POST',
        body: JSON.stringify({
          requestingAgent: agentCode,
          targetAgent: editTarget.OID,
          name, email, phone, selectedRegion,
          selectedBranch: branch,
        }),
      });

      if (data.success) {
        toast.success('Agent details updated');
        setShowEditModal(false);
        setEditTarget(null);
        fetchMyAgents();
      } else if (data.exists) {
        toast.error('That email is already in use by another account');
      } else {
        toast.error(data.message || 'Failed to update agent');
      }
    } catch (err) {
      toast.error('Network error — please try again');
    } finally {
      setEditButtonText('Save Changes');
    }
  }, [editTarget, editForm, agentCode, apiFetch, fetchMyAgents]);

  // ══════════════════════════════════════
  //  SUSPEND / ACTIVATE
  // ══════════════════════════════════════
  const openStatusModal = useCallback((row) => {
    setStatusTarget(row);
    setStatusButtonText(row.Approved === 1 ? 'YES, DEACTIVATE AGENT' : 'YES, ACTIVATE AGENT');
    setShowStatusModal(true);
  }, []);

  const confirmToggleStatus = useCallback(async () => {
    if (!statusTarget) return;
    const nextApproved = statusTarget.Approved === 1 ? 0 : 1;
    try {
      setStatusButtonText(nextApproved === 1 ? 'Activating...' : 'Deactivating...');
      const data = await apiFetch(`${API}/toggleAgentStatus`, {
        method: 'POST',
        body: JSON.stringify({
          requestingAgent: agentCode,
          targetAgent: statusTarget.OID,
          approved: nextApproved,
        }),
      });

      if (data.success) {
        toast.success(nextApproved === 1 ? 'Agent activated' : 'Agent deactivated');
        setShowStatusModal(false);
        setStatusTarget(null);
        fetchMyAgents();
      } else {
        toast.error(data.message || 'Failed to update agent status');
      }
    } catch (err) {
      toast.error('Network error — please try again');
    }
  }, [statusTarget, agentCode, apiFetch, fetchMyAgents]);

  // ══════════════════════════════════════
  //  PRODUCTION
  // ══════════════════════════════════════
  const openProductionModal = useCallback((row) => {
    setProductionTarget(row);
    setProductionProduct('');
    setProductionStart('');
    setProductionEnd('');
    setProductionRows([]);
    setShowProductionModal(true);
  }, []);

  const fetchProduction = useCallback(async () => {
    if (!productionTarget) return;
    try {
      setProductionLoading(true);
      let url = `${API}/getSubAgentProduction?requestingAgent=${agentCode}&agent=${productionTarget.OID}`;
      if (productionProduct) url += `&product=${productionProduct}`;
      if (productionStart) url += `&start=${new Date(productionStart).getTime()}`;
      if (productionEnd) url += `&end=${new Date(productionEnd).getTime()}`;
      const data = await apiFetch(url);
      setProductionRows(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Failed to load production');
    } finally {
      setProductionLoading(false);
    }
  }, [productionTarget, productionProduct, productionStart, productionEnd, agentCode, apiFetch]);

  useEffect(() => {
    if (showProductionModal) fetchProduction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showProductionModal, productionTarget]);

  const productionTotal = useMemo(
    () => productionRows.reduce((sum, r) => sum + Number(r.GrossPremium || 0), 0),
    [productionRows]
  );

  const exportProductionCsv = useCallback(() => {
    if (!productionTarget || productionRows.length === 0) return;

    const headers = ['#', 'Policy No', 'Client / Narration', 'Product', 'Commencement', 'Expiry', 'Gross Premium (KES)', 'Net Commission (KES)'];
    const escapeCsv = (value) => {
      const str = String(value ?? '');
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };

    const rows = productionRows.map((r, i) => [
      i + 1,
      r.PolicyNo,
      r.Narration,
      r.ProductDescription,
      formatISODate(r.CommencementDate),
      formatISODate(r.ExpiryDate),
      formatter(r.GrossPremium),
      formatter(r.NetCommission),
    ]);
    rows.push(['', '', '', '', '', 'Total Gross Premium', formatter(productionTotal), '']);

    const csv = [headers, ...rows].map((row) => row.map(escapeCsv).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${productionTarget.Dealer_name}_production_${formatISODate(new Date())}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [productionTarget, productionRows, productionTotal]);

  // ══════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════
  if (!isSuperAgent) return null; // still checking access, or redirect in flight

  return (
    <div className="ma-page">
      <Header userName={agentName || agent} />
      <Sidebar />
      <ToastContainer position="top-right" autoClose={3000} />

      <main className="ma-main">
        {/* ── Top row ── */}
        <div className="ma-top-row">
          <span className="ma-company">DEFINITE ASSURANCE</span>
          <span className="ma-breadcrumb">My Agents / Definite Assurance</span>
        </div>

        <div className="ma-card">
          <div className="ma-card-header">
            <h3>My Agents</h3>
            <div className="ma-header-actions">
              <button className="ma-btn-outline" onClick={() => navigate('/my-agents/performance')}>
                <FaTrophy style={{ marginRight: 6 }} /> Overall Performance
              </button>
              <button className="ma-add-btn" onClick={toggleAddForm}>
                {showAddForm ? <><FaTimes style={{ marginRight: 6 }} /> Cancel</> : <><FaUserPlus style={{ marginRight: 6 }} /> Add Agent</>}
              </button>
            </div>
          </div>

          {showAddForm && (
            <form className="ma-add-form" onSubmit={handleAddAgent}>
              <div className="ma-form-row">
                <div className="ma-form-group">
                  <label htmlFor="ma-name">Name*</label>
                  <input id="ma-name" type="text" placeholder="Enter agent's name" value={form.name} onChange={updateField('name')} />
                </div>
                <div className="ma-form-group">
                  <label htmlFor="ma-email">Email*</label>
                  <input id="ma-email" type="email" placeholder="Enter email" value={form.email} onChange={updateField('email')} />
                </div>
              </div>

              <div className="ma-form-row">
                <div className="ma-form-group">
                  <label htmlFor="ma-phone">Phone number*</label>
                  <input id="ma-phone" type="tel" placeholder="0712345678" value={form.phone} onChange={updateField('phone')} />
                </div>
                <div className="ma-form-group">
                  <label htmlFor="ma-krapin">KRA PIN*</label>
                  <input id="ma-krapin" type="text" placeholder="KRA PIN" value={form.krapin} onChange={updateField('krapin')} />
                </div>
              </div>

              <div className="ma-form-row">
                <div className="ma-form-group">
                  <label htmlFor="ma-identification">IRA License Number*</label>
                  <input id="ma-identification" type="text" placeholder="IRA License Number" value={form.identification} onChange={updateField('identification')} />
                </div>
                <div className="ma-form-group">
                  <label htmlFor="ma-region">Select Definite Branch</label>
                  <select id="ma-region" value={form.selectedRegion} onChange={updateField('selectedRegion')}>
                    <option value="" disabled>SELECT BRANCH</option>
                    {REGIONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="ma-form-row">
                <div className="ma-form-group">
                  <label htmlFor="ma-branch-region">KCB Region</label>
                  <select id="ma-branch-region" value={form.region} onChange={updateFormRegion}>
                    <option value="" disabled>SELECT REGION</option>
                    {regionOptions.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div className="ma-form-group">
                  <label htmlFor="ma-branch">KCB Branch</label>
                  <select id="ma-branch" value={form.branch} onChange={updateField('branch')} disabled={!form.region}>
                    <option value="" disabled>SELECT BRANCH</option>
                    {branchesForRegion.map((b) => (
                      <option key={b.OID} value={b.OID}>{b.BranchName}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="ma-form-row">
                <div className="ma-form-group" style={{ flex: 1 }}>
                  <label htmlFor="ma-kyc">KYC Documents</label>
                  <input id="ma-kyc" type="file" multiple onChange={handleKycFilesChange} />
                  {kycFiles.length > 0 && (
                    <span className="ma-kyc-file-count">{kycFiles.length} file(s) selected</span>
                  )}
                </div>
              </div>

              <p className="ma-default-password-note">
                The agent will be created with the default portal password and emailed their login details.
              </p>

              <button type="submit" className="ma-submit-btn" disabled={buttonText !== 'Register Agent'}>
                {buttonText}
              </button>
            </form>
          )}

          {/* ── Agents table ── */}
          {agentsLoading ? (
            <div className="ma-loading">
              <div className="ma-spinner" />
              <p>Loading agents...</p>
            </div>
          ) : agents.length === 0 ? (
            <div className="ma-empty">No agents added yet.</div>
          ) : (
            <div className="ma-table-wrap">
              <table className="ma-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Agent</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Agent Code</th>
                    <th>Branch</th>
                    <th>Date Created</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((a, i) => (
                    <tr key={a.OID}>
                      <td>{i + 1}</td>
                      <td>
                        <button className="ma-agent-link" onClick={() => openProductionModal(a)}>
                          {a.Dealer_name}
                        </button>
                      </td>
                      <td>{a.Email}</td>
                      <td>{localPhone(a.Mobile)}</td>
                      <td>{a.Dealer_code}</td>
                      <td>{a.BranchName || '-'}</td>
                      <td>{formatISODate(a.DateCreated)}</td>
                      <td>
                        <span className={`ma-status-pill ${a.Approved === 1 ? 'active' : 'inactive'}`}>
                          {a.Approved === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="ma-row-actions">
                          <button className="ma-icon-btn" onClick={() => openEditModal(a)} title="Edit">
                            <FaEdit /> Edit
                          </button>
                          <button className="ma-icon-btn" onClick={() => openProductionModal(a)} title="View Production">
                            <FaChartLine /> Production
                          </button>
                          <button
                            className={`ma-icon-btn ${a.Approved === 1 ? 'danger' : 'success'}`}
                            onClick={() => openStatusModal(a)}
                            title={a.Approved === 1 ? 'Deactivate' : 'Activate'}
                          >
                            {a.Approved === 1 ? <><FaBan /> Deactivate</> : <><FaCheckCircle /> Activate</>}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* ══════════════════════════════════════
          EDIT AGENT MODAL
         ══════════════════════════════════════ */}
      {showEditModal && editTarget && (
        <div className="ma-modal-overlay">
          <div className="ma-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ma-modal-header">
              <h3>Edit Agent</h3>
              <button className="ma-modal-close" onClick={() => { setShowEditModal(false); setEditTarget(null); }}>
                <FaTimes />
              </button>
            </div>
            <form className="ma-modal-body" onSubmit={handleEditAgent}>
              <div className="ma-form-group" style={{ marginBottom: 14 }}>
                <label>Email*</label>
                <input type="email" value={editForm.email} onChange={updateEditField('email')} />
              </div>
              <div className="ma-form-row">
                <div className="ma-form-group">
                  <label>Name*</label>
                  <input type="text" value={editForm.name} onChange={updateEditField('name')} />
                </div>
                <div className="ma-form-group">
                  <label>Phone number*</label>
                  <input type="tel" value={editForm.phone} onChange={updateEditField('phone')} />
                </div>
              </div>
              <div className="ma-form-row">
                <div className="ma-form-group">
                  <label>KRA PIN</label>
                  <input type="text" value={editForm.krapin} disabled />
                </div>
                <div className="ma-form-group">
                  <label>IRA License Number</label>
                  <input type="text" value={editForm.identification} disabled />
                </div>
              </div>
              <div className="ma-form-row">
                <div className="ma-form-group">
                  <label>Select Definite Branch</label>
                  <select value={editForm.selectedRegion} onChange={updateEditField('selectedRegion')}>
                    <option value="" disabled>SELECT BRANCH</option>
                    {REGIONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="ma-form-row">
                <div className="ma-form-group">
                  <label>KCB Region</label>
                  <select value={editForm.region} onChange={updateEditFormRegion}>
                    <option value="" disabled>SELECT REGION</option>
                    {regionOptions.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div className="ma-form-group">
                  <label>KCB Branch</label>
                  <select value={editForm.branch} onChange={updateEditField('branch')} disabled={!editForm.region}>
                    <option value="" disabled>SELECT BRANCH</option>
                    {editBranchesForRegion.map((b) => (
                      <option key={b.OID} value={b.OID}>{b.BranchName}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="ma-form-group" style={{ marginBottom: 14 }}>
                <label>KYC Documents</label>
                {editKycLoading ? (
                  <span className="ma-kyc-file-count">Loading files…</span>
                ) : editKycFiles.length === 0 ? (
                  <span className="ma-kyc-file-count">No KYC documents uploaded.</span>
                ) : (
                  <ul className="ma-kyc-file-list">
                    {editKycFiles.map((fileName) => (
                      <li key={fileName} onClick={() => openKycFile(fileName)}>
                        <FaFileAlt /> <span>{fileName}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="ma-kyc-upload-row">
                  <input type="file" multiple onChange={handleEditKycFilesChange} disabled={editKycUploading} />
                  <button
                    type="button"
                    className="ma-btn-outline"
                    onClick={handleUploadEditKyc}
                    disabled={editKycUploading || editKycNewFiles.length === 0}
                  >
                    <FaUpload style={{ marginRight: 6 }} />
                    {editKycUploading ? 'Uploading…' : 'Upload'}
                  </button>
                </div>
              </div>

              <div className="ma-modal-actions">
                <button type="button" className="ma-btn-outline" onClick={() => { setShowEditModal(false); setEditTarget(null); }}>
                  Cancel
                </button>
                <button type="submit" className="ma-submit-btn" disabled={editButtonText !== 'Save Changes'}>
                  {editButtonText}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
          SUSPEND / ACTIVATE CONFIRM MODAL
         ══════════════════════════════════════ */}
      {showStatusModal && statusTarget && (
        <div className="ma-modal-overlay">
          <div className="ma-modal ma-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="ma-modal-header">
              <h3>{statusTarget.Approved === 1 ? 'Deactivate Agent' : 'Activate Agent'}</h3>
              <button className="ma-modal-close" onClick={() => { setShowStatusModal(false); setStatusTarget(null); }}>
                <FaTimes />
              </button>
            </div>
            <div className="ma-modal-body">
              <p>
                Are you sure you want to {statusTarget.Approved === 1 ? 'deactivate' : 'activate'} <strong>{statusTarget.Dealer_name}</strong>?
                {statusTarget.Approved === 1 && ' They will be unable to log in to the portal while inactive.'}
              </p>
              <div className="ma-modal-actions">
                <button className="ma-btn-outline" onClick={() => { setShowStatusModal(false); setStatusTarget(null); }}>
                  No, Go Back
                </button>
                <button
                  className={`ma-submit-btn ${statusTarget.Approved === 1 ? 'danger' : ''}`}
                  onClick={confirmToggleStatus}
                  disabled={statusButtonText.includes('...')}
                >
                  {statusButtonText}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
          PRODUCTION MODAL
         ══════════════════════════════════════ */}
      {showProductionModal && productionTarget && (
        <div className="ma-modal-overlay">
          <div className="ma-modal ma-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="ma-modal-header">
              <h3>{productionTarget.Dealer_name} — Production</h3>
              <button className="ma-modal-close" onClick={() => { setShowProductionModal(false); setProductionTarget(null); }}>
                <FaTimes />
              </button>
            </div>
            <div className="ma-modal-body">
              <div className="ma-filter-bar">
                <div className="ma-filter-group">
                  <label>Product</label>
                  <select value={productionProduct} onChange={(e) => setProductionProduct(e.target.value)}>
                    <option value="">All Products</option>
                    {PRODUCTS.map((p) => (
                      <option key={p.code} value={p.code}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div className="ma-filter-group">
                  <label>From</label>
                  <input type="date" value={productionStart} onChange={(e) => setProductionStart(e.target.value)} />
                </div>
                <div className="ma-filter-group">
                  <label>To</label>
                  <input type="date" value={productionEnd} onChange={(e) => setProductionEnd(e.target.value)} />
                </div>
                <button className="ma-add-btn" onClick={fetchProduction} disabled={productionLoading}>
                  {productionLoading ? 'Loading...' : 'Apply Filters'}
                </button>
              </div>

              {productionLoading ? (
                <div className="ma-loading">
                  <div className="ma-spinner" />
                  <p>Loading production...</p>
                </div>
              ) : productionRows.length === 0 ? (
                <div className="ma-empty">No production found for the selected filters.</div>
              ) : (
                <>
                  <div className="ma-table-wrap">
                    <table className="ma-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Policy No</th>
                          <th>Client / Narration</th>
                          <th>Product</th>
                          <th>Commencement</th>
                          <th>Expiry</th>
                          <th>Gross Premium (KES)</th>
                          <th>Net Commission (KES)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productionRows.map((r, i) => (
                          <tr key={`${r.PolicyNo}-${i}`}>
                            <td>{i + 1}</td>
                            <td>{r.PolicyNo}</td>
                            <td>{r.Narration}</td>
                            <td>{r.ProductDescription}</td>
                            <td>{formatISODate(r.CommencementDate)}</td>
                            <td>{formatISODate(r.ExpiryDate)}</td>
                            <td>{formatter(r.GrossPremium)}</td>
                            <td>{formatter(r.NetCommission)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="ma-production-footer">
                    <button className="ma-btn-outline" onClick={exportProductionCsv}>
                      <FaFileCsv style={{ marginRight: 6 }} /> Export CSV
                    </button>
                    <p className="ma-production-total">
                      Total Gross Premium: <strong>KES {formatter(productionTotal)}</strong>
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
